import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaSave, FaTimes } from 'react-icons/fa';
import config from '../../config';

const ProfileLinksManager = ({ userId }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLink, setEditingLink] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [newLink, setNewLink] = useState({ link_name: '', link_url: '' });
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLinks();
  }, [userId]);

  const fetchLinks = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${config.API_URL}/users/${userId}/links`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setLinks(data);
      }
    } catch (err) {
      setError('Failed to fetch links');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (link) => {
    setEditingLink({ ...link });
    setAddingNew(false);
  };

  const handleSave = async (link) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${config.API_URL}/users/${userId}/links/${link.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          link_name: link.link_name,
          link_url: link.link_url,
          order_index: link.order_index
        })
      });

      if (response.ok) {
        const updatedLink = await response.json();
        setLinks(links.map(l => l.id === updatedLink.id ? updatedLink : l));
        setEditingLink(null);
      }
    } catch (err) {
      setError('Failed to update link');
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this link?')) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${config.API_URL}/users/${userId}/links/${linkId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setLinks(links.filter(l => l.id !== linkId));
      }
    } catch (err) {
      setError('Failed to delete link');
    }
  };

  const handleAddNew = async () => {
    if (!newLink.link_name || !newLink.link_url) {
      setError('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${config.API_URL}/users/${userId}/links`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          link_name: newLink.link_name,
          link_url: newLink.link_url,
          order_index: links.length
        })
      });

      if (response.ok) {
        const createdLink = await response.json();
        setLinks([...links, createdLink]);
        setNewLink({ link_name: '', link_url: '' });
        setAddingNew(false);
      }
    } catch (err) {
      setError('Failed to add link');
    }
  };

  if (loading) return <div className="text-gray-400">Loading links...</div>;

  return (
    <div className="mt-6 border-t border-gray-700 pt-6">
      <h3 className="text-lg font-semibold mb-4">Profile Links</h3>
      
      {error && (
        <div className="mb-4 p-3 bg-red-900 text-red-200 rounded-md text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {links.map((link) => (
          <div key={link.id} className="bg-gray-900 p-3 rounded-lg">
            {editingLink?.id === link.id ? (
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={editingLink.link_name}
                  onChange={(e) => setEditingLink({ ...editingLink, link_name: e.target.value })}
                  className="flex-1 p-2 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                  placeholder="Link name"
                />
                <input
                  type="url"
                  value={editingLink.link_url}
                  onChange={(e) => setEditingLink({ ...editingLink, link_url: e.target.value })}
                  className="flex-1 p-2 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                  placeholder="URL"
                />
                <button
                  onClick={() => handleSave(editingLink)}
                  className="p-2 bg-green-600 hover:bg-green-700 rounded text-white"
                >
                  <FaSave />
                </button>
                <button
                  onClick={() => setEditingLink(null)}
                  className="p-2 bg-gray-600 hover:bg-gray-700 rounded text-white"
                >
                  <FaTimes />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{link.link_name}</p>
                  <p className="text-gray-400 text-sm">{link.link_url}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(link)}
                    className="p-2 bg-blue-600 hover:bg-blue-700 rounded text-white"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(link.id)}
                    className="p-2 bg-red-600 hover:bg-red-700 rounded text-white"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {addingNew ? (
          <div className="bg-gray-900 p-3 rounded-lg">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={newLink.link_name}
                onChange={(e) => setNewLink({ ...newLink, link_name: e.target.value })}
                className="flex-1 p-2 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                placeholder="Link name (e.g., Twitter)"
              />
              <input
                type="url"
                value={newLink.link_url}
                onChange={(e) => setNewLink({ ...newLink, link_url: e.target.value })}
                className="flex-1 p-2 bg-gray-800 border border-gray-600 rounded text-white text-sm"
                placeholder="URL (e.g., https://twitter.com/username)"
              />
              <button
                onClick={handleAddNew}
                className="p-2 bg-green-600 hover:bg-green-700 rounded text-white"
              >
                <FaSave />
              </button>
              <button
                onClick={() => {
                  setAddingNew(false);
                  setNewLink({ link_name: '', link_url: '' });
                }}
                className="p-2 bg-gray-600 hover:bg-gray-700 rounded text-white"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setAddingNew(true)}
            className="w-full p-3 bg-gray-900 hover:bg-gray-800 border border-gray-700 rounded-lg text-gray-400 flex items-center justify-center space-x-2"
          >
            <FaPlus />
            <span>Add New Link</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileLinksManager;